import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Stack,
  InputAdornment,
  CircularProgress,
  Fade,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useRTL } from '../../context/RTLContext';
import { translations } from '../../translations/referralModal';
import { submitReferral } from '../../services/api';
import PhoneInput from '../PhoneInput/PhoneInput';

interface ReferralModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormData {
  patientName: string;
  patientEmail: string;
  patientPhone: string;
  providerName: string;
  providerClinic: string;
}

interface FormErrors {
  patientName?: string;
  patientEmail?: string;
  patientPhone?: string;
  providerName?: string;
  providerClinic?: string;
}

const ReferralModal: React.FC<ReferralModalProps> = ({ open, onClose }) => {
  const { language, direction } = useRTL();
  const [formData, setFormData] = useState<FormData>({
    patientName: '',
    patientEmail: '',
    patientPhone: '',
    providerName: '',
    providerClinic: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [contactMethod, setContactMethod] = useState<'email' | 'phone'>('email');

  const lang = language.split('-')[0] as keyof typeof translations;
  const t = translations[lang] || translations.en;

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.patientName.trim()) {
      newErrors.patientName = t.validation.required;
    }

    // Check based on selected contact method
    if (contactMethod === 'email') {
      if (!formData.patientEmail.trim()) {
        newErrors.patientEmail = t.validation.required;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.patientEmail)) {
        newErrors.patientEmail = t.validation.invalidEmail;
      }
    } else {
      if (!formData.patientPhone.trim()) {
        newErrors.patientPhone = t.validation.required;
      } else if (!/^\+?[\d\s-()]+$/.test(formData.patientPhone)) {
        newErrors.patientPhone = t.validation.invalidPhone;
      }
    }

    if (!formData.providerName.trim()) {
      newErrors.providerName = t.validation.required;
    }

    if (!formData.providerClinic.trim()) {
      newErrors.providerClinic = t.validation.required;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateReferralCode = (name: string): string => {
    const prefix = 'TIB';
    const timestamp = Date.now().toString(36).toUpperCase();
    const nameCode = name.substring(0, 3).toUpperCase().replace(/[^A-Z]/g, 'X');
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    return `${prefix}-${nameCode}${timestamp}-${random}`;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const code = generateReferralCode(formData.patientName);
      
      // Submit referral to backend
      await submitReferral({
        ...formData,
        referralCode: code,
        language,
        timestamp: new Date().toISOString()
      });

      setReferralCode(code);
      setIsSuccess(true);
    } catch (error) {
      console.error('Failed to submit referral:', error);
      // In production, show error message
      // For now, still show success for demo purposes
      const code = generateReferralCode(formData.patientName);
      setReferralCode(code);
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setFormData({
      patientName: '',
      patientEmail: '',
      patientPhone: '',
      providerName: '',
      providerClinic: ''
    });
    setErrors({});
    setIsSuccess(false);
    setReferralCode('');
    setCopied(false);
    setContactMethod('email');
    onClose();
  };

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 40px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {isSuccess ? t.success.title : t.title}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {!isSuccess ? (
          <Fade in={!isSuccess}>
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                {t.incentiveMessage}
              </Alert>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t.subtitle}
              </Typography>

              <Stack spacing={3}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {t.sections.yourInfo}
                </Typography>

                <TextField
                  fullWidth
                  label={t.fields.patientName}
                  value={formData.patientName}
                  onChange={handleChange('patientName')}
                  error={!!errors.patientName}
                  helperText={errors.patientName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />

                {/* Contact Method Selection */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                    {t.fields.contactPrompt}
                  </Typography>
                  <ToggleButtonGroup
                    value={contactMethod}
                    exclusive
                    onChange={(_, newMethod) => {
                      if (newMethod !== null) {
                        setContactMethod(newMethod);
                        // Clear errors for the method not selected
                        if (newMethod === 'email') {
                          setErrors({ ...errors, patientPhone: undefined });
                        } else {
                          setErrors({ ...errors, patientEmail: undefined });
                        }
                      }
                    }}
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    <ToggleButton value="email" sx={{ py: 1.5 }}>
                      <EmailIcon sx={{ mr: 1 }} />
                      {t.fields.contactEmail}
                    </ToggleButton>
                    <ToggleButton value="phone" sx={{ py: 1.5 }}>
                      <PhoneIcon sx={{ mr: 1 }} />
                      {t.fields.contactPhone}
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {/* Show email or phone input based on selection */}
                  {contactMethod === 'email' ? (
                    <TextField
                      fullWidth
                      label={t.fields.patientEmail}
                      type="email"
                      value={formData.patientEmail}
                      onChange={handleChange('patientEmail')}
                      error={!!errors.patientEmail}
                      helperText={errors.patientEmail || t.fields.emailHint}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  ) : (
                    <PhoneInput
                      value={formData.patientPhone}
                      onChange={(value) => {
                        setFormData({ ...formData, patientPhone: value });
                        if (errors.patientPhone) {
                          setErrors({ ...errors, patientPhone: undefined });
                        }
                      }}
                      error={!!errors.patientPhone}
                      helperText={errors.patientPhone}
                      label={t.fields.patientPhone}
                      fullWidth
                    />
                  )}
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', pt: 2 }}>
                  {t.sections.providerInfo}
                </Typography>

                <TextField
                  fullWidth
                  label={t.fields.providerName}
                  value={formData.providerName}
                  onChange={handleChange('providerName')}
                  error={!!errors.providerName}
                  helperText={errors.providerName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocalHospitalIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  fullWidth
                  label={t.fields.providerClinic}
                  value={formData.providerClinic}
                  onChange={handleChange('providerClinic')}
                  error={!!errors.providerClinic}
                  helperText={errors.providerClinic}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              </Stack>
            </Box>
          </Fade>
        ) : (
          <Fade in={isSuccess}>
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
              
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t.success.message}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                {t.success.description}
              </Typography>

              <Box
                sx={{
                  p: 3,
                  backgroundColor: 'grey.50',
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  mb: 3
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  {t.success.codeLabel}
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      color: 'primary.main',
                      letterSpacing: 1
                    }}
                  >
                    {referralCode}
                  </Typography>
                  <IconButton onClick={handleCopyCode} size="small" color="primary">
                    <ContentCopyIcon />
                  </IconButton>
                </Stack>
                {copied && (
                  <Chip
                    label={t.success.copied}
                    size="small"
                    color="success"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              <Alert severity="success" sx={{ textAlign: 'left' }}>
                <Typography variant="body2">
                  {t.success.nextSteps}
                </Typography>
                <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                  <li>{t.success.step1}</li>
                  <li>{t.success.step2}</li>
                  <li>{t.success.step3}</li>
                </ul>
              </Alert>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        {!isSuccess ? (
          <>
            <Button onClick={handleClose} color="inherit">
              {t.buttons.cancel}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? t.buttons.submitting : t.buttons.submit}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">
            {t.buttons.close}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReferralModal;